import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { formatPhoneNumber } from "utils/formatPhoneNumber";

import PatientProgress from "../../Widgets/PatientProgress";
import { ClientCallsTableWithHooks } from "./ClientCallsTable/ClientCallsTableWithHooks";
import OnboardingStatus from "../../Widgets/OnboardingStatus";
import BasicInformation from "../../BasicInformation";
import ClientTasks from "../../Widgets/ClientTasks";
import CancelSessionDialog from "../../../WeeklyCalendarSessions/CancelSessionDialog";
import {
  getCancellationReasonLabel,
  getKeyCancellationReason,
} from "../../../../constants/cancelReasons";
import {
  getCustomerDetails,
  getUserInsurance,
  getCustomerWorkflowStatus,
  getUser,
  getCustomerUserId,
  getUserBillableTime,
  getBillableTimeLoading,
  getUserPermissionsList,
  getCustomerLoaded,
} from "../../../../selectors";
import actions from "../../../../actions";
import { useResendLoginEmailToast } from "../../../../hooks/useResendLoginEmailToast";
import useGetCancellationReasons from "hooks/api/appointments/useGetCancellationReasons";

const Dashboard = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  useResendLoginEmailToast();

  const customerDetails = useSelector(getCustomerDetails);
  const customerLoaded = useSelector(getCustomerLoaded);
  const workflowStatus = useSelector(getCustomerWorkflowStatus);
  const currentUser = useSelector(getUser);
  const billableTime = useSelector(getUserBillableTime);
  const billableTimeLoading = useSelector(getBillableTimeLoading);
  const userPermissions = useSelector(getUserPermissionsList);
  const userInsurance = useSelector(getUserInsurance);
  const customerUserId = useSelector(getCustomerUserId);

  const [cancelSessionOpen, setCancelSessionOpen] = useState(false);
  const [sessionDetails, setSessionDetails] = useState({});
  const [cancelReasonText, setCancelReasonText] = useState("");
  const [cancellationReasonId, setCancellationReasonId] = useState("");
  const [responsibleForCancellation, setResponsibleForCancellation] = useState("");
  const [cancelAllInstances, setCancelAllInstances] = useState(false);
  const {
    cancellationReasons,
    isLoading: cancellationReasonsLoading,
  } = useGetCancellationReasons();
  const {
    cancellationReasons: recurringCancellationReasons,
    isLoading: recurringCancellationReasonsLoading,
  } = useGetCancellationReasons(true);

  const isActive = customerDetails?.details?.is_active;
  const clientId = customerDetails?.details?.client_id;

  // Fetch section-specific data
  useEffect(() => {
    if (clientId) {
      dispatch(actions.getBillableTime({ clientId, period: "week" }));
    }
  }, [clientId, dispatch]);

  const resetCancelState = () => {
    setCancelReasonText("");
    setCancellationReasonId("");
    setResponsibleForCancellation("");
    setCancelAllInstances(false);
  };

  const onChangeCancelReason = (e) => {
    const reasons = cancelAllInstances ? recurringCancellationReasons : cancellationReasons;
    const cancellationReason = (reasons || []).find(
      (reason) => String(reason.id) === String(e.target.value)
    );
    if (cancellationReason) {
      setCancellationReasonId(cancellationReason.id);
      setCancelReasonText(cancellationReason.displayLabel);
      setResponsibleForCancellation(cancellationReason.responsibleForCancellation);
      return;
    }

    setCancellationReasonId("");
    setCancelReasonText(e.target.value);
  };

  const onChangeResponsibleForCancellation = (e) => {
    const reasons = cancelAllInstances ? recurringCancellationReasons : cancellationReasons;
    if (Array.isArray(reasons) && reasons.length > 0) {
      setCancellationReasonId("");
      setResponsibleForCancellation(e.target.value);
      setCancelReasonText("");
      return;
    }

    const label = getCancellationReasonLabel(e.target.value);
    setCancellationReasonId("");
    setResponsibleForCancellation(e.target.value);
    if (label === e.target.value) {
      setCancelReasonText("");
    } else {
      setCancelReasonText(label);
    }
  };

  const onChangeCancelSelection = (e) => {
    setCancelAllInstances(e.target.value === "true");
    setCancellationReasonId("");
    setCancelReasonText("");
    setResponsibleForCancellation("");
  };

  const submitCancelSession = () => {
    const activeCancellationReasons = cancelAllInstances
      ? recurringCancellationReasons
      : cancellationReasons;
    const selectedCatalogReason = (activeCancellationReasons || []).find(
      (reason) => String(reason.id) === String(cancellationReasonId)
    );
    const selectedCatalogReasonId = selectedCatalogReason?.id;
    const params = {
      message: cancelReasonText,
      responsibleForCancellation: getKeyCancellationReason(responsibleForCancellation),
      ...(selectedCatalogReasonId !== undefined
        ? { cancellationReasonId: selectedCatalogReasonId }
        : {}),
      id: sessionDetails.id,
      clinicianUserId: sessionDetails.clinician_user_id,
      clientId: sessionDetails.client_id,
      cancelAllInstances,
    };
    dispatch(actions.cancelVideoCall(params));
    setCancelSessionOpen(false);
    setSessionDetails({});
    resetCancelState();
  };

  return (
    <div style={{ overflowX: "hidden" }}>
      <OnboardingStatus customerLoaded={customerLoaded} />
      <BasicInformation
        customerDetails={customerDetails}
        customerLoaded={customerLoaded}
        formatPhoneNumber={formatPhoneNumber}
        userInsurance={userInsurance}
        workflowStatus={workflowStatus}
      />
      <ClientTasks userId={customerUserId} clientId={clientId} />
      {userPermissions?.view_client_call_schedule && (
        <ClientCallsTableWithHooks
          clientId={clientId}
          navigate={history.push}
          userPermissions={userPermissions}
          customerDetails={customerDetails}
          customerLoaded={customerLoaded}
          editDisabled={!isActive || workflowStatus === "SELF_SERVICE_INTAKE"}
          currentUser={currentUser}
          onClickCancelSession={(videoCall) => {
            setSessionDetails(videoCall);
            setCancelSessionOpen(true);
          }}
        />
      )}
      {(billableTime?.length > 0 || billableTimeLoading) && (
        <PatientProgress
          setBillableTimePeriod={(period) =>
            dispatch(
              actions.getBillableTime({ clientId: customerDetails?.details?.client_id, period })
            )
          }
          billableTime={billableTime}
          billableTimeLoading={billableTimeLoading}
          customerLoaded={customerLoaded}
        />
      )}
      <CancelSessionDialog
        open={cancelSessionOpen}
        sessionDetails={sessionDetails}
        closeDialog={() => {
          setCancelSessionOpen(false);
          setSessionDetails({});
          resetCancelState();
        }}
        submitCancelSession={submitCancelSession}
        cancelReasonText={cancelReasonText}
        responsibleForCancellation={responsibleForCancellation}
        cancelAllInstances={cancelAllInstances}
        onChangeCancelReason={onChangeCancelReason}
        onChangeResponsibleForCancellation={onChangeResponsibleForCancellation}
        cancellationReasonId={cancellationReasonId}
        cancellationReasons={cancellationReasons}
        recurringCancellationReasons={recurringCancellationReasons}
        cancellationReasonsLoading={cancellationReasonsLoading}
        recurringCancellationReasonsLoading={recurringCancellationReasonsLoading}
        onChangeCancelSelection={onChangeCancelSelection}
      />
    </div>
  );
};

export default Dashboard;
